/**
 * SearchSpring Infinite Scroll Module
 *
 * IntersectionObserver-based infinite scroll for SS-powered collection and search pages.
 * Mirrors Horizon's native PaginatedList behavior:
 * - Sentinel element triggers next page fetch 100px before entering viewport
 * - Appends new cards to the grid (no full-page replace)
 * - Updates browser history via pushState
 * - Prefetches next page during requestIdleCallback
 * - Resets on filter/sort changes
 *
 * Loaded as standalone defer script by ss-collection.liquid / ss-search-results.liquid.
 * Depends on: window.renderProductGrid, window.SSURLState
 */

(function () {
  'use strict';

  class SSInfiniteScroll {
    #gridList;
    #sentinel;
    #observer;
    #controller;
    #config;
    #currentPage = 1;
    #totalPages = 1;
    #isFetching = false;
    #prefetchedPage = null;
    #prefetchedResponse = null;
    #loadingEl = null;
    // Bumped on every init/reset. In-flight fetches capture the value before
    // awaiting and discard their result if it changed — prevents a fetch that
    // was issued before a filter/sort change from polluting the new results.
    #generation = 0;

    /**
     * @param {object} options
     * @param {object} options.controller - SSCollectionController or SSSearchController instance
     * @param {object} options.config - Parsed ss-config JSON
     */
    constructor({ controller, config }) {
      this.#controller = controller;
      this.#config = config;
    }

    /**
     * Called after first results render. Sets up the sentinel and observer.
     * If the URL has ?page=N (back navigation), backfills pages 2..N and scrolls to page N.
     */
    async init(response) {
      this.#gridList = document.querySelector('#ss-results-grid ul[ref="grid"]');
      if (!this.#gridList) return;

      this.#generation++;
      this.#updatePageState(response);
      this.#createSentinel();
      this.#createLoadingIndicator();

      // Check if URL indicates a target page > 1 (back navigation scenario).
      // The controller already loaded page 1; we need to backfill pages 2..targetPage.
      const targetPage = this.#getTargetPageFromURL();

      if (targetPage > 1 && targetPage <= this.#totalPages) {
        await this.#restoreToPage(targetPage);
      }

      this.#observeSentinel();
      this.#prefetchNext();
    }

    /**
     * Called on every ss:results-rendered (including filter/sort changes).
     * Resets state and re-observes if this is a fresh page-1 render.
     */
    onResultsRendered(response) {
      if (!this.#gridList) {
        // First render — full init
        this.init(response);
        return;
      }

      // Any page-1 response here is a fresh, full grid replacement (filter,
      // sort, clear, or popstate) — infinite-scroll appends fire
      // ss:cards-appended instead, so they never reach this handler. Always
      // reset on page 1: this clears the prefetch cache, which otherwise holds
      // an *unfiltered* page-2 response when the user filters while still on
      // page 1, causing wrong products to append on scroll until a manual
      // refresh (HAN-396).
      const page = response.pagination?.page || response.pagination?.currentPage || 1;
      if (page === 1) {
        this.#reset(response);
        return;
      }

      // Otherwise just update page state (handles edge cases)
      this.#updatePageState(response);
    }

    // -------------------------------------------------------------------------
    // Sentinel & Observer
    // -------------------------------------------------------------------------

    #createSentinel() {
      // Remove existing sentinel if present
      this.#sentinel?.remove();

      this.#sentinel = document.createElement('span');
      this.#sentinel.id = 'ss-infinite-scroll-sentinel';
      this.#sentinel.setAttribute('aria-hidden', 'true');
      // Insert after the grid list's parent (inside the results container)
      this.#gridList.parentElement.after(this.#sentinel);
    }

    #createLoadingIndicator() {
      if (this.#loadingEl) return;

      this.#loadingEl = document.createElement('div');
      this.#loadingEl.className = 'ss-infinite-scroll-loading';
      this.#loadingEl.hidden = true;
      this.#loadingEl.innerHTML =
        '<div class="ss-infinite-scroll-spinner">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.4 31.4" />' +
        '</svg>' +
        '</div>';

      this.#sentinel.before(this.#loadingEl);
    }

    #observeSentinel() {
      if (this.#observer) {
        this.#observer.disconnect();
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#loadNextPage();
            }
          }
        },
        { rootMargin: '100px' }
      );

      if (this.#sentinel) {
        this.#observer.observe(this.#sentinel);
      }
    }

    // -------------------------------------------------------------------------
    // Page Loading
    // -------------------------------------------------------------------------

    async #loadNextPage() {
      if (this.#isFetching) return;
      if (this.#currentPage >= this.#totalPages) return;

      this.#isFetching = true;
      const gen = this.#generation;
      const nextPage = this.#currentPage + 1;

      // Show loading indicator
      if (this.#loadingEl) this.#loadingEl.hidden = false;

      let response;

      // Use prefetched response if available for this page
      if (this.#prefetchedPage === nextPage && this.#prefetchedResponse) {
        response = this.#prefetchedResponse;
        this.#prefetchedPage = null;
        this.#prefetchedResponse = null;
      } else {
        response = await this.#fetchPage(nextPage);
      }

      // Hide loading indicator
      if (this.#loadingEl) this.#loadingEl.hidden = true;

      // A filter/sort change reset us while this page was in flight — discard
      // it so stale results aren't appended onto the freshly-rendered grid.
      if (gen !== this.#generation) {
        this.#isFetching = false;
        return;
      }

      if (!response || !response.results || response.results.length === 0) {
        this.#isFetching = false;
        return;
      }

      const newElements = this.#appendResults(response, nextPage);
      this.#updatePageState(response);
      this.#updateURL(nextPage);

      // Notify beacon tracking to observe the newly appended cards
      document.dispatchEvent(
        new CustomEvent('ss:cards-appended', { detail: { response, elements: newElements } })
      );

      this.#isFetching = false;

      // Prefetch the next page during idle time
      this.#prefetchNext();
    }

    async #fetchPage(pageNum) {
      if (!this.#controller || !this.#controller.fetchPage) return null;
      return this.#controller.fetchPage(pageNum);
    }

    #appendResults(response, page) {
      if (!this.#gridList) return [];

      // Pull the live dedup Set off the controller so appends share state with
      // the initial render. Controller returns null when consolidation is off.
      const seenGids = this.#controller?.seenGids || undefined;

      const html = window.renderProductGrid(response.results, {
        ...this.#config,
        page: page,
        seenGids,
      });

      // Create a temporary container to parse the HTML
      const temp = document.createElement('div');
      temp.innerHTML = html;

      // Append each child element to the grid, collecting refs
      const added = [];
      while (temp.firstElementChild) {
        added.push(this.#gridList.appendChild(temp.firstElementChild));
      }
      return added;
    }

    // -------------------------------------------------------------------------
    // Back-navigation restoration
    // -------------------------------------------------------------------------

    #getTargetPageFromURL() {
      const pageParam = new URL(window.location.href).searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) || 1 : 1;
    }

    /**
     * Fetch pages 2..targetPage in parallel, append in order, then scroll
     * to the first card of the target page.
     */
    async #restoreToPage(targetPage) {
      if (this.#loadingEl) this.#loadingEl.hidden = false;

      const gen = this.#generation;

      // Fetch all pages in parallel
      const pageNums = [];
      for (let p = 2; p <= targetPage; p++) {
        pageNums.push(p);
      }

      const responses = await Promise.all(
        pageNums.map((p) => this.#fetchPage(p))
      );

      // Bail if a filter/sort change reset us while restoring.
      if (gen !== this.#generation) {
        if (this.#loadingEl) this.#loadingEl.hidden = true;
        return;
      }

      // Append in order
      const allNewElements = [];
      for (let i = 0; i < pageNums.length; i++) {
        const resp = responses[i];
        if (!resp || !resp.results || resp.results.length === 0) continue;

        const elements = this.#appendResults(resp, pageNums[i]);
        allNewElements.push(...elements);
      }

      if (this.#loadingEl) this.#loadingEl.hidden = true;

      // Update state to reflect the last loaded page
      this.#currentPage = targetPage;
      this.#updateURL(targetPage);

      // Notify beacon for all appended cards
      if (allNewElements.length > 0) {
        const lastResponse = responses[responses.length - 1];
        document.dispatchEvent(
          new CustomEvent('ss:cards-appended', {
            detail: { response: lastResponse, elements: allNewElements },
          })
        );
      }

      // Scroll to the first card of the target page
      requestAnimationFrame(() => {
        const targetCard = this.#gridList.querySelector(`[data-page="${targetPage}"]`);
        if (targetCard) {
          targetCard.scrollIntoView({ behavior: 'instant' });
        }
      });
    }

    // -------------------------------------------------------------------------
    // Prefetch
    // -------------------------------------------------------------------------

    #prefetchNext() {
      const nextPage = this.#currentPage + 1;
      if (nextPage > this.#totalPages) return;

      // Don't prefetch if we already have this page cached
      if (this.#prefetchedPage === nextPage) return;

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => this.#doPrefetch(nextPage));
      } else {
        setTimeout(() => this.#doPrefetch(nextPage), 200);
      }
    }

    async #doPrefetch(pageNum) {
      // Double-check we still need this page (state may have changed)
      if (pageNum > this.#totalPages) return;
      if (this.#prefetchedPage === pageNum) return;

      const gen = this.#generation;
      const response = await this.#fetchPage(pageNum);
      // Don't cache a prefetch issued before a filter/sort change.
      if (gen !== this.#generation) return;
      if (response) {
        this.#prefetchedPage = pageNum;
        this.#prefetchedResponse = response;
      }
    }

    // -------------------------------------------------------------------------
    // State Management
    // -------------------------------------------------------------------------

    #updatePageState(response) {
      if (!response?.pagination) return;

      this.#currentPage = response.pagination.page || response.pagination.currentPage || 1;
      this.#totalPages = response.pagination.totalPages || 1;
    }

    #updateURL(page) {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }
      // Drop excluded params (filter.collection_handle) arriving in the URL.
      window.SSURLState?.stripExcluded(url.searchParams);
      history.pushState({}, '', url.pathname + (url.search || ''));
    }

    /**
     * Reset after filter/sort change. Clears accumulated cards and restarts from page 1.
     */
    #reset(response) {
      this.#generation++;
      this.#currentPage = 1;
      this.#totalPages = 1;
      this.#prefetchedPage = null;
      this.#prefetchedResponse = null;
      this.#isFetching = false;

      this.#updatePageState(response);

      // Re-observe sentinel (grid may have been replaced)
      this.#gridList = document.querySelector('#ss-results-grid ul[ref="grid"]');
      if (this.#gridList) {
        // Sentinel may have been removed if grid parent changed — recreate
        this.#createSentinel();
        this.#createLoadingIndicator();
        this.#observeSentinel();
        this.#prefetchNext();
      }
    }
  }

  window.SSInfiniteScroll = SSInfiniteScroll;
})();
